/**
 * Data-driven contributor-onboarding checks.
 *
 * The onboarding modal collects three things: a delivery channel (+ SMS
 * consent), a basic profile (name / occupation), and an optional photo. A
 * contributor whose account already carries that data — captured by the app
 * onboarding, an SMS opt-in flow, or a Studio import — must not be walked
 * through the modal again just because the `contributorOnboardingFinished`
 * flag was never stamped by whichever flow saved the data.
 */

interface UserLike {
  personalData?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    occupation?: string;
    zipCode?: string;
    bio?: string;
    phone?: string;
    preferredInterviewChannel?: string;
    smsConsent?: { granted?: boolean; status?: string };
  };
  serviceData?: {
    onboardingFinished?: boolean;
    contributorOnboardingFinished?: boolean;
  };
}

/** True when the user has already established how interviews reach them:
 *  an explicit channel preference, or a resolved SMS consent (either a
 *  grant or a recorded opt-in status). */
export const hasEstablishedChannel = (user?: UserLike | null): boolean => {
  const pd = user?.personalData;
  if (!pd) return false;
  return (
    !!pd.preferredInterviewChannel ||
    pd.smsConsent?.granted === true ||
    !!pd.smsConsent?.status
  );
};

/** True when the profile step's required fields already exist. */
export const hasContributorProfileBasics = (user?: UserLike | null): boolean => {
  const pd = user?.personalData;
  if (!pd) return false;
  return !!(pd.firstName?.trim() && pd.lastName?.trim() && pd.occupation?.trim());
};

/**
 * True when contributor onboarding is fully satisfied: either a finished flag
 * is set, or all the data onboarding would collect (channel + profile basics)
 * already exists. The photo step is optional and never forces onboarding.
 * Used to decide when it's safe to stamp `contributorOnboardingFinished`.
 */
export const hasCompletedContributorOnboarding = (user?: UserLike | null): boolean => {
  if (!user) return false;
  if (
    user.serviceData?.contributorOnboardingFinished ||
    user.serviceData?.onboardingFinished
  ) {
    return true;
  }
  return hasEstablishedChannel(user) && hasContributorProfileBasics(user);
};

/**
 * True when the user has made ANY onboarding progress: a finished flag, an
 * established channel/consent, or saved profile basics. The interview-link
 * flow gates on this — a contributor who already answered part of onboarding
 * elsewhere must never be walked through it again on the way into an
 * interview; only a genuinely blank account sees the modal.
 */
export const hasStartedContributorOnboarding = (user?: UserLike | null): boolean => {
  if (!user) return false;
  if (
    user.serviceData?.contributorOnboardingFinished ||
    user.serviceData?.onboardingFinished
  ) {
    return true;
  }
  return hasEstablishedChannel(user) || hasContributorProfileBasics(user);
};
